import React, { useCallback, useEffect, useState } from "react";

import {
  calculateFootprintingProgress,
  calculateRemovalsProgress,
  calculateTotalProgramPayoutsForYear,
  formatCurrencyRange,
  formatFractionDisplay,
  formatNumericCurrency,
  getFarmIdFromLocalStorage,
  isSingleFieldOutcomes,
  useDeleteFarm,
  useGetAllCarbonProgramOutcomes,
  useGetFieldsByFarmId,
  useGetFootprintingCropFootprints,
  useGetFootprintingFarmFootprint,
  useInventoryImpact,
  useNavigate,
  useTranslation,
  useUserContext,
} from "utils";

import { ACCOUNTING_METHODOLOGY, FarmResponse, SingleFieldOutcomes } from "models";

import { Card, ContextMenu, Drawer, Flex, Icon, LazyLoadImage, Modal, SkeletonWrapper, TagNormal, Tooltip, Message as message } from "components";

import { FarmDetails } from "pages/app/farm/components/FarmDetails";
import { TaskStatusFooter } from "pages/app/home/components/TaskStatusFooter";

interface HomePageFarmCardProps {
  farm: FarmResponse;
  onFarmUpdated: () => void;
  isCurrentFarm?: boolean;
  onFarmSelect?: (farmId: string) => void;
}

interface FarmCardRowProps {
  title: string;
  dataTestId: string;
  handleClick: () => void;
  progress: number;
  value: string;
  hasPayoutRange: boolean;
}

interface ContentRowProps {
  farm: FarmResponse;
  isInventoryProgram: boolean;
}

const FarmCardRow = ({ title, dataTestId, handleClick, progress, value, hasPayoutRange }: FarmCardRowProps) => (
  <Flex
    className="assessment-rows padding-top-50 padding-bottom-100 padding-left-100 padding-right-100"
    data-testid={dataTestId}
    onClick={handleClick}
    gap="1rem"
  >
    <Flex flex={12} className="assessment-cell">
      <p className="paragraph-l margin-0">{title}</p>
    </Flex>
    <Flex flex={hasPayoutRange ? 4 : 5} justify="flex-start">
      <p className="paragraph-l margin-0">{progress}%</p>
    </Flex>
    <Flex flex={hasPayoutRange ? 8 : 5} justify="flex-start" className="overflow-hidden">
      <Tooltip title={value} className="overflow-hidden">
        <p className="paragraph-l overflow-hidden ellipsis margin-0 farm-card-payout">{value}</p>
      </Tooltip>
    </Flex>
    <Flex flex={2} justify="flex-end" align="center">
      <Icon iconName="tabler/chevron-right" />
    </Flex>
  </Flex>
);

const RemovalRow = ({ farm, isInventoryProgram }: ContentRowProps) => {
  useGetFieldsByFarmId({ farmId: farm.id });
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard");
  const currentYear = new Date().getFullYear();
  const { user, switchFarm, reloadUserData } = useUserContext();

  const [shouldNavigateToFarmPage, setShouldNavigateToFarmPage] = useState<boolean>(false);
  const [payout, setPayout] = useState<string>("\u2014");

  const { data: programOutcomes } = useGetAllCarbonProgramOutcomes(farm.id, farm.carbonProgram);
  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(programOutcomes ?? {}).filter(isSingleFieldOutcomes);
  const {
    totalPayoutMin: inventoryPayoutMin,
    totalPayoutMax: inventoryPayoutMax,
    hideInventoryPayouts,
  } = useInventoryImpact(currentYear, singleFieldOutcomes, farm?.id ?? "", farm?.carbonProgram ?? undefined);

  const progress = calculateRemovalsProgress(farm.carbonProgram, programOutcomes, currentYear);

  useEffect(() => {
    if (isInventoryProgram) {
      setPayout(
        hideInventoryPayouts ? "\u2014" : formatCurrencyRange(inventoryPayoutMin, inventoryPayoutMax, { decimals: 0, currency: farm.carbonProgram?.currency })
      );
    } else {
      const { totalProgramPayout: interventionPayout = 0 } = programOutcomes
        ? calculateTotalProgramPayoutsForYear(programOutcomes, currentYear)
        : { totalProgramPayout: 0 };

      setPayout(formatNumericCurrency(interventionPayout, { decimals: 0, currency: farm.carbonProgram?.currency }));
    }
  }, [isInventoryProgram, hideInventoryPayouts, inventoryPayoutMin, inventoryPayoutMax, programOutcomes, currentYear, farm.carbonProgram?.currency]);

  useEffect(() => {
    if (!shouldNavigateToFarmPage) return;
    if (farm.id !== user?.farm.id) return;

    setShouldNavigateToFarmPage(false);
    navigate("/all-fields");
  }, [navigate, shouldNavigateToFarmPage, user?.farm.id, farm.id]);

  const handleFarmClick = () => {
    const localFarmId = getFarmIdFromLocalStorage();

    if (localFarmId !== farm.id) {
      switchFarm(farm.id);
      reloadUserData();
    }
    setShouldNavigateToFarmPage(true);
  };

  return (
    <FarmCardRow
      dataTestId="farm-list-card-removals-row"
      title={t("farmCard.removals")}
      handleClick={handleFarmClick}
      progress={progress || 0}
      value={payout}
      hasPayoutRange={isInventoryProgram}
    />
  );
};

const FootprintingRow = ({ farm, isInventoryProgram }: ContentRowProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard");
  const currentYear = new Date().getFullYear();
  const { user, switchFarm, reloadUserData } = useUserContext();
  const [shouldNavigateToFootprintingPage, setShouldNavigateToFootprintingPage] = useState<boolean>(false);

  const farmProgressQuery = useGetFootprintingFarmFootprint(farm.id, currentYear);
  const cropProgressQueries = useGetFootprintingCropFootprints(farm.id, [currentYear], farm.carbonProgram?.farmLevelFootprinting?.programCrops);

  const { completionRate, totalPayout } = calculateFootprintingProgress(
    farmProgressQuery.data,
    cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data)
  );

  useEffect(() => {
    if (!shouldNavigateToFootprintingPage) return;
    if (farm.id !== user?.farm.id) return;

    setShouldNavigateToFootprintingPage(false);
    navigate("/footprinting");
  }, [navigate, shouldNavigateToFootprintingPage, user?.farm.id, farm.id]);

  const handleFarmClick = () => {
    const localFarmId = getFarmIdFromLocalStorage();

    if (localFarmId !== farm.id) {
      switchFarm(farm.id);
      reloadUserData();
    }
    setShouldNavigateToFootprintingPage(true);
  };

  return (
    <FarmCardRow
      dataTestId="farm-list-card-footprinting-row"
      title={t("farmCard.footprinting")}
      handleClick={handleFarmClick}
      progress={formatFractionDisplay(completionRate) || 0}
      value={formatNumericCurrency(totalPayout, { decimals: 0, currency: farm.carbonProgram?.currency })}
      hasPayoutRange={isInventoryProgram}
    />
  );
};

const HomePageFarmCard = ({ farm, isCurrentFarm = false, onFarmSelect }: HomePageFarmCardProps) => {
  const { t } = useTranslation(["dashboard", "farm", "onboarding"]);

  const { switchFarm } = useUserContext();
  const { mutate: deleteFarm, isPending } = useDeleteFarm();
  const { isLoading } = useGetAllCarbonProgramOutcomes(farm.id, farm.carbonProgram);

  const [deleteFarmPassword, setDeleteFarmPassword] = useState("");
  const [showDeleteModalConfirm, setShowDeleteModalConfirm] = useState(false);
  const [showFarmDetailsDrawer, setShowFarmDetailsDrawer] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);

  const isInventoryProgram = farm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const handleCardClick = () => {
    if (onFarmSelect) {
      onFarmSelect(farm.id);
    }
  };

  const handleModalCancel = () => {
    setShowDeleteModalConfirm(false);
  };

  const handleDrawerClose = () => {
    setShowFarmDetailsDrawer(false);
  };

  const handleFarmDetailsFinish = () => {
    setShowFarmDetailsDrawer(false);
  };

  const handleDeleteFarm = useCallback(async () => {
    if (!deleteFarmPassword) {
      message({ content: t("errors.mandatoryFields"), icon: "error" });
      return;
    }
    deleteFarm(
      {
        password: deleteFarmPassword,
        farmId: farm.id,
      },
      {
        onSuccess: () => {
          message({
            content: t("farm:farmDelete.deleted", { deletedFarm: farm.name }),
            icon: "tick",
          });
          // Set the farmId in the local storage to null after each deletion.
          switchFarm(null);
          setShowDeleteModalConfirm(false);
        },
        onError: () => {
          message({
            content: t("farm:farmDelete.error.delete", { deletedFarm: farm.name }),
            icon: "error",
          });
        },
      }
    );
  }, [deleteFarmPassword, deleteFarm, farm.id, farm.name, t, switchFarm]);

  const shouldShowFootprintingRow = farm.carbonProgram?.platformFeatures?.footprinting?.enabled === true;
  const shouldShowRemovalsRow = farm.carbonProgram?.platformFeatures?.fieldManagement?.removalsExperience ?? true; // for any CP that does not want removals.

  const actions = [
    {
      icon: "tabler/edit",
      label: t("farm:farmContextMenu.editFarm"),
      onClick: () => setShowFarmDetailsDrawer(true),
      "data-testid": "farm-edit-action",
    },
    {
      icon: "tabler/trash",
      label: t("farm:farmContextMenu.deleteFarm"),
      onClick: () => setShowDeleteModalConfirm(true),
      "data-testid": "farm-delete-action",
    },
  ];

  return (
    <>
      <SkeletonWrapper type="block" isLoading={isLoading} data-testid="skeleton">
        <Card dataTestId="home-farm-card" className={`farm-card ${isCurrentFarm ? "current-farm" : ""}`} onClick={handleCardClick}>
          <Flex vertical className="padding-top-50 padding-bottom-50">
            <Flex className="padding-top-50 padding-bottom-50 padding-left-100 padding-right-100" justify="space-between" align="center" gap="1rem">
              <Flex className="overflow-hidden" align="center" gap="1rem">
                <Flex align="center" justify="center" className="farm-card-img">
                  <LazyLoadImage src="/images/homepage/farm-icon.png" alt="Farm crop icon" />
                </Flex>
                <Tooltip className="overflow-hidden" title={farm.name}>
                  <h3 className="overflow-hidden ellipsis">{farm.name}</h3>
                </Tooltip>
              </Flex>
              <Flex align="center">
                {farm.carbonProgram?.programName && (
                  <Tooltip title={t("farmCard.infoMessageOnFarmCardTooltip")}>
                    <TagNormal
                      text={farm.carbonProgram?.programName}
                      color="yellow"
                      className="farm-card-tag text-black-600 padding-top-50 padding-bottom-50 padding-left-100 padding-right-100"
                      icon="tabler/info-circle"
                      dataTestId="farm-info-tag"
                    />
                  </Tooltip>
                )}
                <ContextMenu
                  items={actions.map((action) => ({
                    iconName: action.icon,
                    label: action.label,
                    onClick: action.onClick,
                  }))}
                  placement="bottom-right"
                  visible={showActionMenu}
                  onClose={() => setShowActionMenu(false)}
                  dataTestId="farm-actions-context-menu"
                  iconPlacement="center"
                >
                  <div
                    data-testid="farm-actions-context-menu-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActionMenu(true);
                    }}
                  >
                    <Icon iconName="tabler/dots-vertical" strokeWidth={2} className="pointer flex" />
                  </div>
                </ContextMenu>
              </Flex>
            </Flex>
            <Flex className="padding-left-100 padding-top-50 padding-bottom-100 padding-right-100" data-testid="grid-header" gap="1rem">
              <Flex flex={12} data-testid="assessment-header">
                <p className="margin-0 paragraph-s grey-600-text">{t("dashboard:farmCard.assessment")}</p>
              </Flex>
              <Flex flex={isInventoryProgram ? 4 : 5} data-testid="progress-header">
                <p className="margin-0 paragraph-s grey-600-text">{t("dashboard:farmCard.progress")}</p>
              </Flex>
              <Flex flex={isInventoryProgram ? 8 : 5} data-testid="payout-header">
                <p className="margin-0 paragraph-s grey-600-text">{t("dashboard:farmCard.payout")}</p>
              </Flex>
              <Flex flex={2}>&#8203;</Flex>
            </Flex>
            {shouldShowRemovalsRow && <RemovalRow farm={farm} isInventoryProgram={isInventoryProgram} />}
            {shouldShowFootprintingRow && <FootprintingRow farm={farm} isInventoryProgram={isInventoryProgram} />}
          </Flex>
          <Flex>
            <TaskStatusFooter farm={farm} />
          </Flex>
        </Card>
      </SkeletonWrapper>

      <Modal
        cancelText={t("farm:farmDelete.cancel")}
        description={t("farm:farmDelete.description")}
        inputType="password"
        input={{
          initialValue: "",
          name: "password",
          rules: [{ required: true }],
          label: t("farm:farmDelete.password"),
          onChange: (e) => setDeleteFarmPassword(e.target.value),
        }}
        okText={t("farm:farmDelete.confirm")}
        okButtonProps={{ "data-testid": "confirm-delete-button" }}
        onCancel={handleModalCancel}
        onOk={handleDeleteFarm}
        title={t("farm:farmDelete.title")}
        showModal={showDeleteModalConfirm}
        loading={isPending}
      />

      {showFarmDetailsDrawer && (
        <Drawer
          data-testid="farm-details-drawer"
          visible={showFarmDetailsDrawer}
          title={t("onboarding:onboardingFormHeader.editFarm")}
          onClose={handleDrawerClose}
          footer={undefined}
        >
          <FarmDetails selectedFarmId={farm.id} onFinish={handleFarmDetailsFinish} isOnboarding={false} handleBack={handleFarmDetailsFinish} />
        </Drawer>
      )}
    </>
  );
};

export default HomePageFarmCard;
